/**
 * 初回イベント作成チュートリアルの進行状態 Provider。
 *
 * このチュートリアルは CreateEvent ページから EventDetail ページへ跨いで進むため、
 * ページのローカル state では保持できない。Context（メモリ）で保持しつつ、
 * リロードでツアーが壊れないよう sessionStorage にも退避する。
 *
 * 完了フラグ自体（再表示を防ぐ）はサーバの completedTutorials で管理し、
 * ここでは「いま進行中のフェーズ」と「チュートリアルで作成したイベントID」のみを扱う。
 *
 * sessionStorage はタブ単位で共有されるため、別アカウントへ切り替えた際に前ユーザーの
 * 状態を引き継がないよう、キーをユーザー単位に分離し、ユーザー変化時は `key` で
 * Provider を作り直す（loadInitial を再実行させる）。
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  EventTutorialContext,
  type EventTutorialContextValue,
  type EventTutorialPhase,
} from '@/contexts/eventTutorialContextObj';

interface PersistShape {
  phase: EventTutorialPhase;
  tutorialEventId: string | null;
}

const STORAGE_PREFIX = 'tutorial:event_flow:';

function storageKey(userKey: string): string {
  return `${STORAGE_PREFIX}${userKey}`;
}

function loadInitial(userKey: string): PersistShape {
  try {
    const raw = sessionStorage.getItem(storageKey(userKey));
    if (raw) {
      const parsed = JSON.parse(raw) as PersistShape;
      if (parsed && typeof parsed.phase === 'string') return parsed;
    }
  } catch {
    /* ignore */
  }
  return { phase: 'idle', tutorialEventId: null };
}

export function EventTutorialProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // ユーザーが変わると key が変わり、InnerProvider が作り直されて状態が分離される。
  const userKey = user?.id ?? 'guest';
  return (
    <InnerProvider key={userKey} userKey={userKey}>
      {children}
    </InnerProvider>
  );
}

function InnerProvider({ userKey, children }: { userKey: string; children: ReactNode }) {
  const [state, setState] = useState<PersistShape>(() => loadInitial(userKey));

  // 状態変化を sessionStorage に同期（リロード時の復元用）
  useEffect(() => {
    try {
      if (state.phase === 'idle') {
        sessionStorage.removeItem(storageKey(userKey));
      } else {
        sessionStorage.setItem(storageKey(userKey), JSON.stringify(state));
      }
    } catch {
      /* ignore */
    }
  }, [state, userKey]);

  const setPhase = useCallback((phase: EventTutorialPhase) => {
    setState((prev) => (prev.phase === phase ? prev : { ...prev, phase }));
  }, []);

  const setTutorialEventId = useCallback((eventId: string) => {
    setState((prev) => ({ ...prev, tutorialEventId: eventId }));
  }, []);

  const startCreateTour = useCallback(() => {
    setState({ phase: 'create-ai-button', tutorialEventId: null });
  }, []);

  const endTour = useCallback(() => {
    setState({ phase: 'idle', tutorialEventId: null });
  }, []);

  const value = useMemo<EventTutorialContextValue>(
    () => ({
      phase: state.phase,
      tutorialEventId: state.tutorialEventId,
      isActive: state.phase !== 'idle',
      startCreateTour,
      setPhase,
      setTutorialEventId,
      endTour,
    }),
    [state.phase, state.tutorialEventId, startCreateTour, setPhase, setTutorialEventId, endTour],
  );

  return (
    <EventTutorialContext.Provider value={value}>
      {children}
    </EventTutorialContext.Provider>
  );
}
